import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle2, Edit } from 'lucide-react';
import { mapProductCategoryToJobCategory } from '@/lib/utils/categoryMapping';
import { supabase } from '@/integrations/supabase/client';

interface AISpecificationSummaryProps {
  extractedData: {
    specification_name: string;
    specification_data: Record<string, Record<string, any>>;
    additional_notes?: string;
    productCategoryId: string;
  };
  productCategoryName: string;
  onSave: (specData: any) => void;
  onRevise: () => void;
  onCancel: () => void;
}

export function AISpecificationSummary({
  extractedData,
  productCategoryName,
  onSave,
  onRevise,
  onCancel
}: AISpecificationSummaryProps) {
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({});
  const [fieldNames, setFieldNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchNames = async () => {
      const categoryIds = Object.keys(extractedData.specification_data || {});
      const fieldIds = Object.values(extractedData.specification_data || {})
        .flatMap(fields => Object.keys(fields));

      if (categoryIds.length > 0) {
        const { data: categories } = await supabase
          .from('specification_categories')
          .select('id, name')
          .in('id', categoryIds);

        const catMap: Record<string, string> = {};
        categories?.forEach(c => catMap[c.id] = c.name);
        setCategoryNames(catMap);
      }

      if (fieldIds.length > 0) {
        const { data: fields } = await supabase
          .from('specification_field_definitions')
          .select('id, field_label, field_name')
          .in('id', fieldIds);

        const fieldMap: Record<string, string> = {};
        fields?.forEach(f => fieldMap[f.id] = f.field_label || f.field_name);
        setFieldNames(fieldMap);
      }
    };

    fetchNames();
  }, [extractedData]);

  const handleConfirm = () => {
    const specData = {
      specification_name: extractedData.specification_name,
      specification_description: `AI-generated specification for ${productCategoryName}`,
      category: mapProductCategoryToJobCategory(productCategoryName),
      specification_type: 'personal',
      content: {
        product_category_id: extractedData.productCategoryId,
        specification_data: extractedData.specification_data,
        additional_notes: extractedData.additional_notes || '',
      },
      sections: extractedData.additional_notes ? [{
        title: 'Additional Notes',
        content: extractedData.additional_notes,
        required: false
      }] : [],
      required_fields: [],
      tags: ['ai-generated', productCategoryName.toLowerCase()],
    };

    onSave(specData);
  };

  const categoryCount = Object.keys(extractedData.specification_data || {}).length;
  const fieldCount = Object.values(extractedData.specification_data || {})
    .reduce((sum, category) => sum + Object.keys(category).length, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Review Your Specification</h3>
          <p className="text-sm text-muted-foreground">
            Confirm the details below or continue chatting to make changes
          </p>
        </div>
        <Badge variant="secondary" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {fieldCount} fields captured
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Specification Name</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-medium">{extractedData.specification_name}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Captured Information</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion 
            type="multiple" 
            defaultValue={Object.keys(extractedData.specification_data || {})}
            className="space-y-2"
          >
            {Object.entries(extractedData.specification_data || {}).map(([categoryId, fields]) => (
              <AccordionItem key={categoryId} value={categoryId}>
                <AccordionTrigger className="text-sm font-semibold">
                  {categoryNames[categoryId] || `Category ${categoryId.slice(0, 8)}`}
                  <Badge variant="outline" className="ml-2">
                    {Object.keys(fields).length} fields
                  </Badge>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pt-2">
                    {Object.entries(fields).map(([fieldId, value]) => (
                      <div key={fieldId} className="flex justify-between items-start p-2 rounded-md bg-muted/50">
                        <span className="text-sm font-medium text-muted-foreground">
                          {fieldNames[fieldId] || `Field ${fieldId.slice(0, 8)}`}:
                        </span>
                        <span className="text-sm ml-2 text-right">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {extractedData.additional_notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{extractedData.additional_notes}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between gap-2">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onRevise} className="gap-2">
            <Edit className="h-4 w-4" />
            Continue Editing
          </Button>
        </div>
        <Button onClick={handleConfirm} className="gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Save Specification
        </Button>
      </div>
    </div>
  );
}
